<template>
  <div v-if="filteredPosts.length" class="category-shelf mb-8">
    <div class="d-flex align-center justify-space-between mb-3 px-1">
      <div class="d-flex align-center ga-2">
        <h3 class="text-h6 font-weight-bold">{{ category }}</h3>
        <v-chip size="x-small" color="primary" variant="tonal" class="font-weight-bold">
          {{ filteredPosts.length }}
        </v-chip>
      </div>

      <!-- Carousel Navigation Controls -->
      <div class="d-flex align-center ga-1">
        <v-btn
          icon
          size="x-small"
          variant="tonal"
          class="shelf-nav-btn"
          aria-label="Scroll left"
          @click="scrollLeft"
        >
          <v-icon size="18">mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn
          icon
          size="x-small"
          variant="tonal"
          class="shelf-nav-btn"
          aria-label="Scroll right"
          @click="scrollRight"
        >
          <v-icon size="18">mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Shelf Track -->
    <div ref="trackRef" class="shelf-carousel-track">
      <div
        v-for="post in filteredPosts"
        :key="post.id"
        class="shelf-item"
      >
        <router-link
          :to="{ name: 'MovieDetail', params: { id: post.id } }"
          class="movie-poster-card poster-card poster-card--movies d-block"
        >
          <div class="poster-image-container">
            <v-img
              :src="posterSrc(post)"
              aspect-ratio="2/3"
              cover
              class="rounded-lg w-100 poster-img"
            >
              <template #placeholder>
                <div class="d-flex flex-column align-center justify-center fill-height fallback-poster">
                  <v-icon size="36" color="primary">mdi-movie-open</v-icon>
                  <span class="text-caption text-center px-2 mt-1 text-truncate w-100 font-weight-medium">
                    {{ post.title }}
                  </span>
                </div>
              </template>
            </v-img>

            <!-- Badges Overlay -->
            <div class="poster-badges-top">
              <div v-if="post.rating && Number(post.rating) > 0" class="rating-badge">
                <v-icon size="12" color="amber" class="mr-0.5">mdi-star</v-icon>
                <span>{{ post.rating }}</span>
              </div>
              <div v-if="post.quality" class="quality-badge">
                {{ post.quality }}
              </div>
            </div>

            <!-- Bottom Title & Info Gradient Overlay -->
            <div class="poster-overlay-bottom">
              <div class="poster-title text-truncate font-weight-bold">{{ post.title }}</div>
              <div class="poster-subtitle d-flex justify-space-between align-center">
                <span v-if="post.year">{{ post.year }}</span>
                <span v-if="post.duration" class="ml-auto">{{ post.duration }}</span>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { posterSrc } from '@/utils/movieForm'

export default {
  name: 'RowCategory',
  props: {
    category: { type: String, required: true },
    posts: { type: Array, default: () => [] },
  },
  setup(props) {
    const trackRef = ref(null)

    const scrollLeft = () => {
      if (trackRef.value) {
        trackRef.value.scrollBy({ left: -360, behavior: 'smooth' })
      }
    }

    const scrollRight = () => {
      if (trackRef.value) {
        trackRef.value.scrollBy({ left: 360, behavior: 'smooth' })
      }
    }

    return {
      trackRef,
      scrollLeft,
      scrollRight,
    }
  },
  computed: {
    filteredPosts() {
      return this.posts
        .filter((post) => post.category === this.category)
        .slice()
        .sort((a, b) => new Date(b.created) - new Date(a.created))
    },
  },
  methods: {
    posterSrc,
  },
}
</script>

<style scoped>
.category-shelf {
  position: relative;
}

.shelf-nav-btn {
  border-radius: 50% !important;
}

.shelf-carousel-track {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 4px 4px 14px 4px;
  scrollbar-width: thin;
}

.shelf-item {
  flex: 0 0 155px;
  max-width: 155px;
}

@media (max-width: 600px) {
  .shelf-item {
    flex: 0 0 120px;
    max-width: 120px;
  }
}

.movie-poster-card {
  text-decoration: none;
  color: inherit;
}

.poster-image-container {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  background: rgba(128, 128, 128, 0.08);
}

.fallback-poster {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(168, 85, 247, 0.1));
}

.poster-badges-top {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 2;
}

.rating-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
}

.quality-badge {
  background: rgba(99, 102, 241, 0.85);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 5px;
  border-radius: 4px;
  letter-spacing: 0.03em;
  margin-left: auto;
}

.poster-overlay-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px 8px 8px 8px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 60%, transparent 100%);
  color: #fff;
  z-index: 2;
  transition: opacity 0.2s ease;
}

.poster-title {
  font-size: 0.8rem;
  line-height: 1.2;
}

.poster-subtitle {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}
</style>

